import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../../../components/helpers/Translator';
import {
  getResponsibleAnswer,
  getReviewState,
} from '../../../tools/totalEnergiesRiskActionPlanTools';
import colors from '../../../../../../../constants/colors';
import minions from '../../../../../../../minions/minions';
import useIsMounted from '../../../../../../components/hooks/useIsMounted';
import { axiosPath } from '../../../../../../../routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import _ from 'lodash';

export default function ReviewFields({
  values = {},
  errors = {},
  setFieldValue = () => {},
  isReadOnly = false,
  files = {},
}) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const locale = useSelector((state) => state.global?.urlData?.lang);
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="no" />
      ),
      value: false,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="yes" />
      ),
      value: true,
    },
  ]);
  const [responsibleAnswerOptions] = useState(
    Array.from({ length: 3 }, (_, i) => ({
      value: i,
      label: getResponsibleAnswer(i),
    }))
  );

  const handleUpload = useCallback(
    (opt) => {
      const fs = Array.from(opt).filter((f) => !values.files.includes(f.name));
      setFieldValue('files', [...values.files, ...fs]);
    },
    [values, setFieldValue]
  );

  const toggleDeleteFile = useCallback(
    (salt) => {
      const updateDeletedFiles = values?.deletedFiles;

      if (updateDeletedFiles.includes(salt)) {
        setFieldValue(
          'deletedFiles',
          updateDeletedFiles.filter((s) => s !== salt)
        );
      } else {
        updateDeletedFiles.push(salt);
        setFieldValue('deletedFiles', updateDeletedFiles);
      }
    },
    [values, setFieldValue]
  );

  const handleDownload = useCallback(
    async (salt) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_totalEnergiesRisk_actionPlan_review_fileDownload"
          ),
          {
            moduleBridgeHex,
            reviewHex: values?.hex,
            salt,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            window.open(data, "_blank");
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [values]
  );

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-lg-6">
          <AppTextArea
            title="Descripción de revisión"
            value={values.description}
            onChange={(value) => setFieldValue('description', value)}
            error={errors.description}
            isReadOnly={
              isReadOnly ||
              values?.state > 0 ||
              userBridge?.permissions?.level !== 0
            }
            height="36px"
          />
          {!isReadOnly &&
          values?.state === 0 &&
          userBridge?.permissions?.level === 0 ? (
            <AppDatePicker
              locale={locale}
              title="Fecha prevista revisión"
              value={values.scheduledReviewDate}
              onChange={(opt) => setFieldValue('scheduledReviewDate', opt)}
              minDate={Moment()
                // .add(1, 'days')
                .toDate()}
              showYearDropdown
              showMonthDropdown
            />
          ) : (
            <AppInput
              title="Fecha prevista revisión"
              value={values.scheduledReviewDate}
              isReadOnly
            />
          )}
        </div>
        <div className="col-lg-6">
          <AppInput
            title="Estado revisión de plan de acción"
            value={getReviewState(values?.state)}
            isReadOnly
          />
          {!isReadOnly &&
          values?.state === 0 &&
          userBridge?.permissions?.level === 0 ? (
            <AppSingleSelect
              options={noYesOptions}
              title="Requiere documentación"
              value={values?.requireDocumentation}
              error={errors?.requireDocumentation}
              onChange={(opt) => {
                setFieldValue('requireDocumentation', opt);
              }}
            />
          ) : (
            <AppInput
              title="Requiere documentación"
              value={
                values?.requireDocumentation
                  ? noYesOptions[1]?.label
                  : noYesOptions[0]?.label
              }
              isReadOnly
            />
          )}
        </div>
      </div>
      {values?.state >= 1 && (
        <div className="row">
          <div className="col-lg-6">
            <AppInput
              title="Fecha comunicación revisión"
              value={values?.announcementDate || '--'}
              isReadOnly
            />
          </div>
          <div className="col-lg-6">
            <AppInput
              title="Comunicada revisión a la persona responsable"
              value={
                values?.announced
                  ? noYesOptions[1]?.label
                  : noYesOptions[0]?.label
              }
              isReadOnly
            />
          </div>
          <div className="col-lg-6">
            {!isReadOnly &&
            values?.state === 1 &&
            values?.responsibleID === userBridge?.id ? (
              <AppSingleSelect
                options={responsibleAnswerOptions}
                title="Respuesta persona responsable"
                value={values?.responsibleAnswer}
                error={errors?.responsibleAnswer}
                onChange={(opt) => {
                  setFieldValue('responsibleAnswer', opt);
                }}
                required
              />
            ) : (
              <AppInput
                title="Respuesta persona responsable"
                value={getResponsibleAnswer(values?.responsibleAnswer) || '--'}
                isReadOnly
              />
            )}
          </div>
          <div className="col-lg-6">
            <AppTextArea
              title="Observaciones persona responsable"
              value={values.responsibleObservations}
              onChange={(value) =>
                setFieldValue('responsibleObservations', value)
              }
              error={errors.responsibleObservations}
              resize="vertical"
              isReadOnly={
                isReadOnly ||
                values?.state >= 2 ||
                values?.responsibleID !== userBridge?.id
              }
            />
          </div>
        </div>
      )}
      {values?.state >= 2 && (
        <div className="row">
          <div className="col-lg-6">
            <AppInput
              title="Fecha respuesta persona responsable"
              value={values?.responsibleAnswerDate || '--'}
              isReadOnly
            />
          </div>
          <div className="col-lg-6">
            <AppInput
              title="Fecha comunicación área Compliance y CI"
              value={values?.complianceAreaCommunicationDate || '--'}
              isReadOnly
            />
          </div>
          <div className="col-lg-6">
            {!isReadOnly &&
            values?.state === 2 &&
            userBridge?.permissions?.level === 0 ? (
              <AppSingleSelect
                options={responsibleAnswerOptions}
                title="Resultado validación revisión plan de acción"
                value={values?.controlReviewValidationResult}
                error={errors?.controlReviewValidationResult}
                onChange={(opt) => {
                  setFieldValue('controlReviewValidationResult', opt);
                }}
                required
              />
            ) : (
              <AppInput
                title="Resultado validación revisión plan de acción"
                value={
                  getResponsibleAnswer(values?.controlReviewValidationResult) ||
                  '--'
                }
                isReadOnly
              />
            )}
          </div>
          <div className="col-lg-6">
            <AppTextArea
              title="Observaciones área Compliance y CI"
              value={values.complianceAreaObservations}
              onChange={(value) =>
                setFieldValue('complianceAreaObservations', value)
              }
              error={errors.complianceAreaObservations}
              height="12px"
              isReadOnly={
                isReadOnly ||
                values?.state !== 2 ||
                userBridge?.permissions?.level !== 0
              }
            />
          </div>
        </div>
      )}

      <div className="row">
        {!isReadOnly &&
          values?.state === 1 &&
          values?.responsibleID === userBridge?.id && (
            <div className="col-lg-6">
              <AppFileUploader
                placeholder={
                  <Translator
                    file="admin"
                    name="general.select-one-more-files"
                  />
                }
                onChange={handleUpload}
                error={errors?.files}
                isMulti
              />
              {errors?.files && (
                <span className="text-danger">*{errors.files}</span>
              )}
            </div>
          )}
        <div
          className={
            !isReadOnly &&
            values?.state === 1 &&
            values?.responsibleID === userBridge?.id
              ? 'col-lg-6'
              : 'col-lg-6 offset-lg-6'
          }
        >
          <FileList uploaded>
            {_.map(files, (f, salt) => (
              <li key={salt}>
                {!isReadOnly &&
                  values?.state === 1 &&
                  values?.responsibleID === userBridge?.id && (
                    <span
                      className="pointer"
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="click.delete"
                        />
                      )}
                      onClick={() => toggleDeleteFile(salt)}
                    >
                      <AppRemixIcon
                        className="pointer mr-3"
                        icon="delete-bin"
                        color={
                          values?.deletedFiles?.includes(salt)
                            ? colors.grey400
                            : colors.danger
                        }
                      />
                    </span>
                  )}
                {minions.files.fileIcon(
                  f?.name,
                  true,
                  values?.deletedFiles?.includes(salt) ? colors.grey400 : null,
                  'mr-2'
                )}
                <span className="pointer" onClick={() => handleDownload(salt)}>
                  {f?.name}
                </span>
              </li>
            ))}
          </FileList>
          {!isReadOnly &&
            values?.state === 1 &&
            values?.responsibleID === userBridge?.id && (
              <FileList>
                {values?.files.map((f, i) => (
                  <li key={i}>
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="click.delete"
                        />
                      )}
                      onClick={() =>
                        setFieldValue(
                          'files',
                          values?.files?.filter((f, index) => index !== i)
                        )
                      }
                    >
                      <AppRemixIcon
                        className="pointer mr-3"
                        icon="delete-bin"
                        color={colors.danger}
                      />
                    </span>
                    {minions.files.fileIcon(f?.name, true, null, 'mr-2')}
                    <span>{f?.name}</span>
                  </li>
                ))}
              </FileList>
            )}
        </div>
      </div>
    </div>
  );
}

const FileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.uploaded ? colors.grey200 : 'initial')};
  li {
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
